import { API_URLS } from '_constants';
import { httpClient } from 'lib';
import { idbService, uploadService, offlineModeService } from 'utils/services';
import { parseAssignmentsData } from 'utils/custom';
import { reloadVocabulary } from 'modules/Media/tags/mediaTagsStore';
import * as TYPES from './Collect.types';
import { getOfflineAssignments } from './Collect.selectors';
import { getProfileData } from '../Auth/Auth.actions';
import { createVisit } from '../Visit/Visit.actions';

/**
 * Sequence number for the paginated list requests below.
 *
 * Their reducers overwrite the slice on every success, so whichever response lands LAST wins --
 * regardless of which request it answers. Sorting twice in a row on a slow connection therefore
 * left the table showing an older ordering than the one the server had just returned. Tagging each
 * request lets the reducer drop a response that is no longer the current one.
 */
let listRequestId = 0;
const nextRequestId = () => {
  listRequestId += 1;
  return listRequestId;
};

export const getCampaignList = (params) => {
  const shouldReadFresh = localStorage.getItem('fresh_campaign') === 'true';
  if (shouldReadFresh) {
    localStorage.removeItem('fresh_campaign');
  }
  return {
    types: [TYPES.GET_CAMPAIGN_LIST, TYPES.GET_CAMPAIGN_LIST_SUCCESS, TYPES.GET_CAMPAIGN_LIST_FAIL],
    promise: (client) =>
      client.get(
        `${API_URLS.campaigns}?${params}&include=account,agency,campaign_manager,form&status=0${
          shouldReadFresh ? '&fresh=true' : ''
        }`,
      ),
  };
};

export const getAssignmentsList = (params) => {
  const shouldClearCache = localStorage.getItem('cc') === 'true';
  if (shouldClearCache) {
    localStorage.removeItem('cc');
  }

  return {
    requestId: nextRequestId(),
    types: [TYPES.GET_ASSIGNMENT_LIST, TYPES.GET_ASSIGNMENT_LIST_SUCCESS, TYPES.GET_ASSIGNMENT_LIST_FAIL],
    promise: (client) =>
      client.get(
        `${API_URLS.assignments}?${params}&include=retailer,form,users,campaign&is_active=true&status=0${
          shouldClearCache ? '&cc=true' : ''
        }`,
      ),
  };
};

// attach to campaign
export const getAssignmnetsAttachedToCampaign = (params) => ({
  types: [TYPES.GET_ASSIGNMENT_SEARCH, TYPES.GET_ASSIGNMENT_SEARCH_SUCCESS, TYPES.GET_ASSIGNMENT_SEARCH_FAIL],
  promise: (client) => client.get(`${API_URLS.campaigns}?${params}&not_archived=true&include=agency`),
});

export const getRevisitsList = (params, timeZone) => ({
  requestId: nextRequestId(),
  types: [TYPES.GET_REVISITS_LIST, TYPES.GET_REVISITS_LIST_SUCCESS, TYPES.GET_REVISITS_LIST_FAIL],
  promise: (client) => client.get(`${API_URLS.revisits}?${params}&status=0`),
  timeZone,
});

/**
 * Count only, for the Revisits badge in the sidebar.
 *
 * Deliberately NOT getRevisitsList with a small page size: that writes the rows and the pagination
 * of the list slice, so it both flashed its one row into the table and could land after a real list
 * response and replace it. This has its own reducer branch that touches nothing but the count.
 */
export const getRevisitsCount = () => ({
  types: [TYPES.GET_REVISITS_COUNT, TYPES.GET_REVISITS_COUNT_SUCCESS, TYPES.GET_REVISITS_COUNT_FAIL],
  promise: (client) => client.get(`${API_URLS.revisits}?page=1&page_size=1&status=0`),
});

export const getRevisitsCSV = (params) => ({
  types: [TYPES.GET_REVISITS_CSV, TYPES.GET_REVISITS_CSV_SUCCESS, TYPES.GET_REVISITS_CSV_FAIL],
  promise: (client) => client.get(`${API_URLS.revisitsCSV}${params}&not_archived=true&status=0`),
});

export const getCampaignListCSV = (params) => ({
  types: [TYPES.GET_CAMPAIGN_LIST_CSV, TYPES.GET_CAMPAIGN_LIST_CSV_SUCCESS, TYPES.GET_CAMPAIGN_LIST_CSV_FAIL],
  promise: (client) => client.get(`${API_URLS.campaignCSV}${params}`),
});

export const getAssignmentsCSV = (params) => ({
  types: [TYPES.GET_ASSIGNMENTS_CSV, TYPES.GET_ASSIGNMENTS_CSV_SUCCESS, TYPES.GET_ASSIGNMENTS_CSV_FAIL],
  promise: (client) => client.get(`${API_URLS.assignmentsCSV}${params}&not_archived=true&is_active=true&status=0`),
});

export const getOfflineAssigmentsList = ({ params = '?' } = {}) => ({
  types: [TYPES.GET_ALL_ASSIGNMENTS_LIST, TYPES.GET_ALL_ASSIGNMENTS_LIST_SUCCESS, TYPES.GET_ALL_ASSIGNMENTS_LIST_FAIL],
  promise: (client) =>
    client.get(
      `${API_URLS.assignments}${params}&ordering=retailer__name&include=retailer,form,users,campaign&is_active=true&status=0&offline=true`,
    ),
});

export const setCollectCount = (payload) => ({
  type: TYPES.SET_COLLECT_COUNT,
  payload,
});

export const archiveCampaigns = (data) => {
  return {
    types: [TYPES.ARCHIVE_CAMPAIGNS, TYPES.ARCHIVE_CAMPAIGNS_SUCCESS, TYPES.ARCHIVE_CAMPAIGNS_FAIL],
    promise: (client) => client.patch(`${API_URLS.campaigns}/archive`, data),
  };
};

export const restoreCampaigns = (data) => {
  return {
    types: [TYPES.RESTORE_CAMPAIGNS, TYPES.RESTORE_CAMPAIGNS_SUCCESS, TYPES.RESTORE_CAMPAIGNS_FAIL],
    promise: (client) => client.patch(`${API_URLS.campaigns}/restore`, data),
  };
};

// --- Offline staging ---------------------------------------------------------

// Hydrates the Redux offline list from IDB. Call on mount of any page that
// needs to display the staged offline assignments.
export const loadOfflineAssignments = () => async (dispatch) => {
  const items = await idbService.getOfflineAssignments();
  dispatch({ type: TYPES.LOAD_OFFLINE_ASSIGNMENTS_SUCCESS, payload: items });
  return items;
};

// Builds the per-form payload in the same shape the SW expects when serving
// `/forms/{id}` from IDB: `{ data: form-resource, included: [{ Agency }] }`.
const buildFormPayload = (form, assignment, included) => {
  const campaignId = assignment.relationships?.campaign?.data?.id;
  const campaign = campaignId ? included.find((item) => item.type === 'Campaign' && item.id === campaignId) : null;
  const agencyId = form.relationships?.agency?.data?.id;
  const agencyIncluded = agencyId
    ? [
        {
          type: 'Agency',
          id: agencyId,
          attributes: { name: campaign?.attributes?.agency_name },
        },
      ]
    : [];
  return { data: { ...form }, included: agencyIncluded };
};

// Stages selected assignments for offline use. Fetches fresh data from the
// backend with &offline=true (which returns full form_json etc. that the
// regular list doesn't include), then writes the per-id assignment + its
// retailer + form into IDB additively so prior offline data is preserved.
// Also refreshes the profile snapshot in IDB so the SW can serve it later.
export const addAssignmentsToOffline = (ids) => async (dispatch) => {
  dispatch({ type: TYPES.ADD_ASSIGNMENTS_TO_OFFLINE_REQUEST, payload: ids });
  try {
    if (!ids || ids.length === 0) {
      throw new Error('No assignments selected.');
    }

    const url =
      `${API_URLS.assignments}?ids=${ids.join(',')}` +
      '&ordering=retailer__name' +
      '&include=retailer,form,users,campaign' +
      '&is_active=true&status=0&offline=true&page_size=50';
    const response = await httpClient.get(url);
    const data = response?.data || [];
    const included = response?.included || [];

    const findIncluded = (type, id) => included.find((item) => item.type === type && item.id === id);

    const writes = [];
    const stagedItems = [];

    data.forEach((assignment) => {
      const retailerId = assignment.relationships?.retailer?.data?.id;
      const formId = assignment.relationships?.form?.data?.id;
      const retailer = retailerId ? findIncluded('Retailer', retailerId) : null;
      const form = formId ? findIncluded('Form', formId) : null;

      // Pre-compute a flattened display row so the Offline Surveys tab can
      // render without re-resolving JSON:API includes from IDB.
      const display = parseAssignmentsData({ data: [assignment], included })[0];
      const stored = { ...assignment, _display: display };

      writes.push(idbService.putOfflineAssignment(assignment.id, stored));
      if (retailer) writes.push(idbService.putRetailer(retailerId, { data: retailer }));
      if (form) writes.push(idbService.putForm(formId, buildFormPayload(form, assignment, included)));

      stagedItems.push({ id: assignment.id, data: stored });
    });

    // Refresh and persist profile so the SW can serve `/profile` offline.
    const profileInfo = await dispatch(getProfileData());
    writes.push(idbService.putProfile(profileInfo));
    // Warm the media tag vocabulary while there is still a network: the survey form tags photos from
    // the picker, and offline the fetch can't run. reloadVocabulary writes the IDB cache itself, and
    // swallows its own failures — an account without the media_tags module simply caches nothing.
    writes.push(reloadVocabulary());
    writes.push(idbService.setReady(true));

    await Promise.all(writes);

    dispatch({ type: TYPES.ADD_ASSIGNMENTS_TO_OFFLINE_SUCCESS, payload: stagedItems });
    return stagedItems;
  } catch (error) {
    dispatch({ type: TYPES.ADD_ASSIGNMENTS_TO_OFFLINE_FAIL, error });
    throw error;
  }
};

// Removes a single staged assignment (called after a successful sync).
export const removeOfflineAssignment = (id) => async (dispatch) => {
  await idbService.removeOfflineAssignment(id);
  dispatch({ type: TYPES.REMOVE_OFFLINE_ASSIGNMENT_SUCCESS, payload: id });
};

// Wipes all offline data + flag. Used by the "Clear offline data" control.
export const clearOfflineAssignments = () => async (dispatch) => {
  await idbService.clearOfflineData();
  dispatch({ type: TYPES.CLEAR_OFFLINE_ASSIGNMENTS });
};

// --- Unsynced surveys (filled offline, waiting to sync) ----------------------

export const loadUnsyncedSurveys = () => async (dispatch) => {
  const items = await idbService.getUnsyncedSurveys();
  dispatch({ type: TYPES.LOAD_UNSYNCED_SURVEYS_SUCCESS, payload: items });
  return items;
};

export const removeUnsyncedSurvey = (key) => async (dispatch) => {
  await idbService.removeUnsyncedSurvey(key);
  dispatch({ type: TYPES.REMOVE_UNSYNCED_SURVEY_SUCCESS, payload: key });
};

// Syncs a single offline survey: uploads any pending files, POSTs the visit,
// clears the survey from IDB, unstages the matching offline assignment, and
// if the offline queue is now empty auto-exits offline mode. On failure the
// survey is left in place so the user can retry.
export const syncOfflineSurvey = (key, surveyData) => async (dispatch, getState) => {
  const visitData = { ...surveyData };
  const offlineId = visitData.data?.attributes?.__offlineId__;

  if (offlineId) {
    const pendingFiles = await idbService.getPendingFiles(offlineId);
    if (pendingFiles && Object.keys(pendingFiles).length > 0) {
      const uploadedFiles = await uploadService.syncOfflineFiles(pendingFiles);
      const attributes = { ...visitData.data.attributes };
      Object.entries(uploadedFiles).forEach(([questionName, fileData]) => {
        attributes[questionName] = [fileData];
      });
      delete attributes.__offlineId__;
      visitData.data = { ...visitData.data, attributes };
    }
    await idbService.removePendingFiles(offlineId);
  }

  await dispatch(createVisit(visitData));
  await idbService.removeUnsyncedSurvey(key);
  dispatch({ type: TYPES.REMOVE_UNSYNCED_SURVEY_SUCCESS, payload: key });

  const assignmentId = surveyData?.data?.relationships?.assignment?.data?.id;
  if (assignmentId) {
    await idbService.removeOfflineAssignment(assignmentId);
    dispatch({ type: TYPES.REMOVE_OFFLINE_ASSIGNMENT_SUCCESS, payload: assignmentId });
  }

  // If the offline queue is now empty, drop out of offline mode so the user
  // doesn't stay on the lone Offline surveys tab with nothing in it.
  const remaining = getOfflineAssignments(getState());
  if (!remaining || remaining.length === 0) {
    await offlineModeService.set(false);
  }
};
